import { z } from 'zod';

import { listSearchSchema } from '@/shared/patterns/list-search';

import { agreementFilters, queueFilters } from './collection.filters';

const ids = z.array(z.string()).optional().catch(undefined);

/** Painel: o recorte de carteiras vive na URL (CollectionDashboardContainer.tsx:66-84). */
export const dashboardSearch = z.object({
  // Código digitado na URL chega como número (o `parseSearch` lê JSON): aceita os dois e guarda texto.
  walletsCode: z
    .preprocess((value) => (Array.isArray(value) ? value : value === undefined ? undefined : [value]), z.array(z.union([z.string(), z.number()]).transform(String).pipe(z.string().regex(/^\d+$/))))
    .optional()
    .catch(undefined),
  sheet: z.enum(['carteiras']).optional().catch(undefined),
});
export type DashboardSearch = z.output<typeof dashboardSearch>;

/** Tamanhos da fila (CollectionQueueList.tsx:404). */
export const QUEUE_PAGE_SIZES = [5, 10, 15, 20, 50, 100] as const;

/**
 * Fila: filtros, a aba como `onlyMine`/`onlyUnassigned` (o recorte que o legado grava na URL,
 * CollectionQueueFilterProvider.tsx:16-20), a seleção e o sheet aberto.
 */
export const queueSearch = listSearchSchema(
  {
    ...queueFilters.shape,
    onlyMine: z.literal('true').optional().catch(undefined),
    onlyUnassigned: z.literal('true').optional().catch(undefined),
    selected: ids,
    sheet: z.enum(['atribuir', 'tratativa']).optional().catch(undefined),
  },
  { sizes: QUEUE_PAGE_SIZES },
);
export type QueueSearch = z.output<typeof queueSearch>;

/** Painel do devedor: parcelas marcadas e o sheet aberto. */
export const debtorSearch = z.object({
  selected: ids,
  sheet: z.enum(['tratativa', 'simular', 'acordo']).optional().catch(undefined),
});
export type DebtorSearch = z.output<typeof debtorSearch>;

export type QueueScope = 'mine' | 'unassigned' | 'all';

/** Aba pela URL: sem recorte é "Todos" (CollectionQueueContainer.tsx:37-42). */
export function scopeOf(search: { readonly onlyMine?: string | undefined; readonly onlyUnassigned?: string | undefined }): QueueScope {
  if (search.onlyMine === 'true') return 'mine';
  if (search.onlyUnassigned === 'true') return 'unassigned';
  return 'all';
}

/** Trocar de aba limpa o recorte anterior, a página e a seleção (CollectionQueueContainer.tsx:132-140). */
export function scopePatch(scope: QueueScope) {
  return {
    onlyMine: scope === 'mine' ? ('true' as const) : undefined,
    onlyUnassigned: scope === 'unassigned' ? ('true' as const) : undefined,
    page: undefined,
    selected: undefined,
  };
}

/** Tamanhos da lista de acordos (AgreementList.tsx:204), os mesmos da fila. */
export const AGREEMENT_PAGE_SIZES = QUEUE_PAGE_SIZES;

/**
 * Lista de acordos: os filtros do formulário e os dois que só chegam pela URL, como no legado
 * (`installmentId` e `onlyMine`, CollectionAgreementFilterProvider.tsx:53-59).
 */
export const agreementsSearch = listSearchSchema(
  {
    ...agreementFilters.shape,
    installmentId: z.string().min(1).max(100).optional().catch(undefined),
    // O link do legado traz `onlyMine=true`, que o `parseSearch` lê como booleano: aceita os dois e guarda texto.
    onlyMine: z.union([z.literal('true'), z.literal(true)]).transform(() => 'true' as const).optional().catch(undefined),
  },
  { sizes: AGREEMENT_PAGE_SIZES },
);
export type AgreementsSearch = z.output<typeof agreementsSearch>;

/** Detalhe do acordo: a gaveta aberta (as transições do cabeçalho, AgreementFormContainer.tsx:77-78). */
export const AGREEMENT_SHEETS = ['fechar-termos', 'formalizar', 'renegociar', 'cancelar'] as const;
export type AgreementSheet = (typeof AGREEMENT_SHEETS)[number];
export const agreementSearch = z.object({
  sheet: z.enum(AGREEMENT_SHEETS).optional().catch(undefined),
});
export type AgreementSearch = z.output<typeof agreementSearch>;
